import { useState } from "react";
import { useIntl } from "react-intl";
import { CareerGraph } from "@/components/save/CareerGraph";
import { SkillsDiagram } from "@/components/save/SkillsDiagram";
import { Section } from "@/components/design/Section";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import type { ProfileComparisonCoreFragment } from "@/graphql/generated/operations";
import { newestStartFirst } from "@/lib/entry-order";
import { CertificateRow, EducationRow, ExpertiseRow, LanguageRow } from "./profile/rows";
import type { UseProfileReferenceDataResult } from "./use-profile-reference-data";

export type ComparisonSection = "experience" | "education" | "expertise" | "certificates" | "languages";
const readOnly = { editable: false, fields: [], onPatch: () => {} };
function dateValue(value: { date?: string | null; precision?: string | null } | null | undefined) {
  return value?.date?.slice(0, value.precision === "YEAR" ? 4 : value.precision === "DAY" ? 10 : 7) ?? "";
}

export function ProfileComparisonSection({ person, kind, references }: {
  person: ProfileComparisonCoreFragment;
  kind: ComparisonSection;
  references: UseProfileReferenceDataResult;
}) {
  const intl = useIntl();
  const [skillsMode, setSkillsMode] = useState<"list" | "diagram">("diagram");
  const id = `comparison-${person.id}-${kind}`;
  const title = intl.formatMessage({ id: `profile.compare.${kind}` });
  const emptyLabel = intl.formatMessage({ id: "profile.compare.noData" });
  if (kind === "experience") return <CareerGraph
    viewToggleId={`${id}-view`}
    experiences={(person.experiences ?? []).map((entry) => ({ ...entry, startDate: dateValue(entry.startDate), endDate: dateValue(entry.endDate) }))}
    title={title} emptyLabel={emptyLabel} editable={false} renderShareControl={() => null}
    countryOptions={references.countries} industryOptions={references.industries}
  />;
  if (kind === "education") {
    const educations = newestStartFirst(person.educations);
    return <Section id={id} title={title} empty={!educations.length} emptyLabel={emptyLabel}>
      <div className="divide-y divide-ink/5">{educations.map((entry) => <EducationRow key={entry.id} {...readOnly}
        entry={{ id: entry.id, field: entry.field ?? "", institution: entry.institution ?? "", degree: entry.degree?.id ?? "", city: entry.city ?? "", country: entry.country?.id ?? "", startDate: dateValue(entry.startDate), endDate: dateValue(entry.endDate) }}
        countryOptions={references.countries}
        degreeOptions={references.degrees.length ? references.degrees : educations.flatMap((e) => e.degree ? [{ value: e.degree.id, label: e.degree.name ?? "" }] : [])}
      />)}</div>
    </Section>;
  }
  if (kind === "expertise") {
    const entries = (person.expertises ?? []).map((entry) => ({ id: entry.id, description: entry.description ?? "", level: String(entry.level ?? ""), position: entry.position ?? 0 }));
    return <Section id={id} title={title} empty={!entries.length} emptyLabel={emptyLabel} right={entries.length ? <SegmentedControl
      id={`${id}-view`} size="sm" value={skillsMode} onChange={setSkillsMode}
      options={[
        { value: "list", label: intl.formatMessage({ id: "profile.skills.viewList" }) },
        { value: "diagram", label: intl.formatMessage({ id: "profile.skills.viewDiagram" }) },
      ]}
    /> : undefined}>
      {skillsMode === "diagram" ? <SkillsDiagram entries={entries} editable={false} /> : <div className="divide-y divide-ink/5">
        {entries.map((entry) => <ExpertiseRow key={entry.id} entry={entry} {...readOnly} />)}
      </div>}
    </Section>;
  }
  if (kind === "certificates") return <Section id={id} title={title} empty={!person.certifications?.length} emptyLabel={emptyLabel}>
    <div className="divide-y divide-ink/5">{person.certifications?.map((entry) => <CertificateRow key={entry.id} {...readOnly}
      entry={{ id: entry.id, name: entry.name ?? "", date: dateValue(entry.date) }} />)}</div>
  </Section>;
  return <Section id={id} title={title} empty={!person.languageSkills?.length} emptyLabel={emptyLabel}>
    <div className="divide-y divide-ink/5">{person.languageSkills?.map((entry) => <LanguageRow key={entry.id} {...readOnly}
      idPrefix={`comparison-${person.id}`} entry={{ id: entry.id, languageTag: entry.languageTag, cefr: entry.cefr ?? "", nativeSpeaker: entry.nativeSpeaker ?? false }} />)}</div>
  </Section>;
}
